// ─── GL Entries (General Ledger) ──────────────────────────────────────────────

export interface MockGLEntry {
    id: string;
    date: string;
    entryNumber: string;
    type: 'SALES' | 'PURCHASE' | 'PAYMENT' | 'RECEIPT' | 'JOURNAL' | 'OPENING';
    debitAccount: string;
    creditAccount: string;
    amount: number;
    description: string;
    referenceId: string | null;
    referenceNumber: string | null;
    gstAmount: number;
    createdBy: string;
}

export const mockGLEntries: MockGLEntry[] = [
    {
        id: 'gl-001',
        date: '2024-04-01',
        entryNumber: 'GL-2024-0001',
        type: 'OPENING',
        debitAccount: 'Cash & Bank',
        creditAccount: 'Capital',
        amount: 500000,
        description: 'Opening balance',
        referenceId: null,
        referenceNumber: null,
        gstAmount: 0,
        createdBy: 'Admin User',
    },
    {
        id: 'gl-002',
        date: '2024-04-05',
        entryNumber: 'GL-2024-0002',
        type: 'PURCHASE',
        debitAccount: 'Raw Material Stock',
        creditAccount: 'Accounts Payable',
        amount: 60600,
        description: 'GRN-2024-0003 TechSupply',
        referenceId: null,
        referenceNumber: 'GRN-2024-0003',
        gstAmount: 10908,
        createdBy: 'Admin User',
    },
    {
        id: 'gl-003',
        date: '2024-04-09',
        entryNumber: 'GL-2024-0003',
        type: 'SALES',
        debitAccount: 'Accounts Receivable',
        creditAccount: 'Sales Revenue',
        amount: 71508,
        description: 'INV-2024-001',
        referenceId: null,
        referenceNumber: 'INV-2024-001',
        gstAmount: 10908,
        createdBy: 'Admin User',
    },
    {
        id: 'gl-004',
        date: '2024-04-10',
        entryNumber: 'GL-2024-0004',
        type: 'RECEIPT',
        debitAccount: 'Cash & Bank',
        creditAccount: 'Accounts Receivable',
        amount: 71508,
        description: 'Payment received INV-2024-001',
        referenceId: null,
        referenceNumber: 'INV-2024-001',
        gstAmount: 0,
        createdBy: 'Admin User',
    },
    {
        id: 'gl-005',
        date: '2024-04-15',
        entryNumber: 'GL-2024-0005',
        type: 'PURCHASE',
        debitAccount: 'Raw Material Stock',
        creditAccount: 'Accounts Payable',
        amount: 385500,
        description: 'GRN-2024-0004',
        referenceId: null,
        referenceNumber: 'GRN-2024-0004',
        gstAmount: 69390,
        createdBy: 'Admin User',
    },
    {
        id: 'gl-006',
        date: '2024-04-20',
        entryNumber: 'GL-2024-0006',
        type: 'JOURNAL',
        debitAccount: 'Production WIP',
        creditAccount: 'Raw Material Stock',
        amount: 35000,
        description: 'Materials issued for PRD-2024-001',
        referenceId: 'prod-001',
        referenceNumber: 'PRD-2024-001',
        gstAmount: 0,
        createdBy: 'Admin User',
    },
    {
        id: 'gl-007',
        date: '2024-04-25',
        entryNumber: 'GL-2024-0007',
        type: 'JOURNAL',
        debitAccount: 'Finished Goods Stock',
        creditAccount: 'Production WIP',
        amount: 45425,
        description: 'Production completed PRD-2024-001',
        referenceId: 'prod-001',
        referenceNumber: 'PRD-2024-001',
        gstAmount: 0,
        createdBy: 'Admin User',
    },
    {
        id: 'gl-008',
        date: '2024-04-28',
        entryNumber: 'GL-2024-0008',
        type: 'PAYMENT',
        debitAccount: 'Accounts Payable',
        creditAccount: 'Cash & Bank',
        amount: 71508,
        description: 'Payment to TechSupply',
        referenceId: null,
        referenceNumber: 'PYMT-2024-001',
        gstAmount: 0,
        createdBy: 'Admin User',
    },
];

// ─── Financial Summary ──────────────────────────────────────────────────────

export interface MockFinancialSummary {
    period: string;
    fromDate: string;
    toDate: string;
    totalSales: number;
    totalPurchases: number;
    totalReceipts: number;
    totalPayments: number;
    grossProfit: number;
    operatingExpenses: number;
    netProfit: number;
    totalAssets: number;
    totalLiabilities: number;
    equity: number;
    gstCollected: number;
    gstPaid: number;
    netGST: number;
}

export const mockFinancialSummary: MockFinancialSummary = {
    period: 'Apr 2024',
    fromDate: '2024-04-01',
    toDate: '2024-04-30',
    totalSales: 456800,
    totalPurchases: 285600,
    totalReceipts: 380000,
    totalPayments: 220000,
    grossProfit: 171200,
    operatingExpenses: 45000,
    netProfit: 126200,
    totalAssets: 850000,
    totalLiabilities: 320000,
    equity: 530000,
    gstCollected: 82224,
    gstPaid: 51408,
    netGST: 30816,
};

// ─── Chart of Accounts (COA) ─────────────────────────────────────────────────

export interface MockAccountGroup {
    id: string;
    code: string;
    name: string;
    type: 'ASSET' | 'LIABILITY' | 'INCOME' | 'EXPENSE' | 'EQUITY';
    parentId: string | null;
    level: number;
    isSystem: boolean;
}

export interface MockAccount {
    id: string;
    code: string;
    name: string;
    groupId: string;
    groupName: string;
    type: 'ASSET' | 'LIABILITY' | 'INCOME' | 'EXPENSE' | 'EQUITY';
    openingBalance: number;
    currentBalance: number;
    isActive: boolean;
    isSystem: boolean;
    allowDirectPosting: boolean;
}

export interface MockJournalVoucher {
    id: string;
    jvNumber: string;
    date: string;
    narration: string;
    status: 'DRAFT' | 'POSTED' | 'CANCELLED';
    entries: MockJVEntry[];
    totalDebit: number;
    totalCredit: number;
    isBalanced: boolean;
    createdBy: string;
    postedAt: string | null;
    referenceType: string | null;
    referenceId: string | null;
}

export interface MockJVEntry {
    id: string;
    jvId: string;
    accountId: string;
    accountCode: string;
    accountName: string;
    debit: number;
    credit: number;
    narration: string | null;
}

export let mockAccountGroups: MockAccountGroup[] = [
    { id: 'ag-001', code: '1000', name: 'Assets', type: 'ASSET', parentId: null, level: 1, isSystem: true },
    { id: 'ag-002', code: '1100', name: 'Current Assets', type: 'ASSET', parentId: 'ag-001', level: 2, isSystem: true },
    { id: 'ag-003', code: '1200', name: 'Fixed Assets', type: 'ASSET', parentId: 'ag-001', level: 2, isSystem: true },
    { id: 'ag-004', code: '2000', name: 'Liabilities', type: 'LIABILITY', parentId: null, level: 1, isSystem: true },
    { id: 'ag-005', code: '2100', name: 'Current Liabilities', type: 'LIABILITY', parentId: 'ag-004', level: 2, isSystem: true },
    { id: 'ag-006', code: '2200', name: 'Long Term Liabilities', type: 'LIABILITY', parentId: 'ag-004', level: 2, isSystem: true },
    { id: 'ag-007', code: '3000', name: 'Equity & Reserves', type: 'EQUITY', parentId: null, level: 1, isSystem: true },
    { id: 'ag-008', code: '4000', name: 'Income', type: 'INCOME', parentId: null, level: 1, isSystem: true },
    { id: 'ag-009', code: '4100', name: 'Direct Income', type: 'INCOME', parentId: 'ag-008', level: 2, isSystem: true },
    { id: 'ag-010', code: '5000', name: 'Expenses', type: 'EXPENSE', parentId: null, level: 1, isSystem: true },
    { id: 'ag-011', code: '5100', name: 'Direct Expenses', type: 'EXPENSE', parentId: 'ag-010', level: 2, isSystem: true },
    { id: 'ag-012', code: '5200', name: 'Indirect Expenses', type: 'EXPENSE', parentId: 'ag-010', level: 2, isSystem: true },
];

export let mockAccounts: MockAccount[] = [
    { id: 'acc-001', code: '1101', name: 'Cash in Hand', groupId: 'ag-002', groupName: 'Current Assets', type: 'ASSET', openingBalance: 50000, currentBalance: 45230, isActive: true, isSystem: true, allowDirectPosting: true },
    { id: 'acc-002', code: '1102', name: 'HDFC Bank - Current A/c', groupId: 'ag-002', groupName: 'Current Assets', type: 'ASSET', openingBalance: 500000, currentBalance: 687450, isActive: true, isSystem: true, allowDirectPosting: true },
    { id: 'acc-003', code: '1103', name: 'Accounts Receivable (Debtors)', groupId: 'ag-002', groupName: 'Current Assets', type: 'ASSET', openingBalance: 150000, currentBalance: 425680, isActive: true, isSystem: true, allowDirectPosting: false },
    { id: 'acc-004', code: '1104', name: 'Stock / Inventory', groupId: 'ag-002', groupName: 'Current Assets', type: 'ASSET', openingBalance: 800000, currentBalance: 920000, isActive: true, isSystem: true, allowDirectPosting: false },
    { id: 'acc-005', code: '1105', name: 'GST Input Credit (ITC)', groupId: 'ag-002', groupName: 'Current Assets', type: 'ASSET', openingBalance: 0, currentBalance: 45000, isActive: true, isSystem: true, allowDirectPosting: false },
    { id: 'acc-006', code: '1201', name: 'Plant & Machinery', groupId: 'ag-003', groupName: 'Fixed Assets', type: 'ASSET', openingBalance: 2500000, currentBalance: 2500000, isActive: true, isSystem: false, allowDirectPosting: false },
    { id: 'acc-007', code: '1202', name: 'Accumulated Depreciation', groupId: 'ag-003', groupName: 'Fixed Assets', type: 'ASSET', openingBalance: -200000, currentBalance: -250000, isActive: true, isSystem: false, allowDirectPosting: false },
    { id: 'acc-008', code: '2101', name: 'Accounts Payable (Creditors)', groupId: 'ag-005', groupName: 'Current Liabilities', type: 'LIABILITY', openingBalance: 120000, currentBalance: 185000, isActive: true, isSystem: true, allowDirectPosting: false },
    { id: 'acc-009', code: '2102', name: 'GST Payable (Output Tax)', groupId: 'ag-005', groupName: 'Current Liabilities', type: 'LIABILITY', openingBalance: 0, currentBalance: 68500, isActive: true, isSystem: true, allowDirectPosting: false },
    { id: 'acc-010', code: '2103', name: 'TDS Payable', groupId: 'ag-005', groupName: 'Current Liabilities', type: 'LIABILITY', openingBalance: 0, currentBalance: 9000, isActive: true, isSystem: true, allowDirectPosting: false },
    { id: 'acc-011', code: '3001', name: 'Owner Capital', groupId: 'ag-007', groupName: 'Equity & Reserves', type: 'EQUITY', openingBalance: 3000000, currentBalance: 3000000, isActive: true, isSystem: true, allowDirectPosting: false },
    { id: 'acc-012', code: '3002', name: 'Retained Earnings', groupId: 'ag-007', groupName: 'Equity & Reserves', type: 'EQUITY', openingBalance: 0, currentBalance: 285000, isActive: true, isSystem: true, allowDirectPosting: false },
    { id: 'acc-013', code: '4101', name: 'Sales Revenue', groupId: 'ag-009', groupName: 'Direct Income', type: 'INCOME', openingBalance: 0, currentBalance: 1250000, isActive: true, isSystem: true, allowDirectPosting: false },
    { id: 'acc-014', code: '5101', name: 'Purchase / COGS', groupId: 'ag-011', groupName: 'Direct Expenses', type: 'EXPENSE', openingBalance: 0, currentBalance: 850000, isActive: true, isSystem: true, allowDirectPosting: false },
    { id: 'acc-015', code: '5201', name: 'Salaries & Wages', groupId: 'ag-012', groupName: 'Indirect Expenses', type: 'EXPENSE', openingBalance: 0, currentBalance: 180000, isActive: true, isSystem: false, allowDirectPosting: true },
    { id: 'acc-016', code: '5202', name: 'Rent & Utilities', groupId: 'ag-012', groupName: 'Indirect Expenses', type: 'EXPENSE', openingBalance: 0, currentBalance: 48000, isActive: true, isSystem: false, allowDirectPosting: true },
    { id: 'acc-017', code: '5203', name: 'Depreciation Expense', groupId: 'ag-012', groupName: 'Indirect Expenses', type: 'EXPENSE', openingBalance: 0, currentBalance: 50000, isActive: true, isSystem: false, allowDirectPosting: false },
    { id: 'acc-018', code: '5204', name: 'Bank Charges', groupId: 'ag-012', groupName: 'Indirect Expenses', type: 'EXPENSE', openingBalance: 0, currentBalance: 3500, isActive: true, isSystem: false, allowDirectPosting: true },
    { id: 'acc-019', code: '4102', name: 'Profit on Disposal of Assets', groupId: 'ag-009', groupName: 'Direct Income', type: 'INCOME', openingBalance: 0, currentBalance: 0, isActive: true, isSystem: false, allowDirectPosting: false },
    { id: 'acc-020', code: '5205', name: 'Loss on Disposal of Assets', groupId: 'ag-012', groupName: 'Indirect Expenses', type: 'EXPENSE', openingBalance: 0, currentBalance: 0, isActive: true, isSystem: false, allowDirectPosting: false },
];

export let mockJournalVouchers: MockJournalVoucher[] = [
    {
        id: 'jv-001', jvNumber: 'JV-2026-0001',
        date: '2026-09-01',
        narration: 'Monthly depreciation entry - Sep 2026',
        status: 'POSTED',
        totalDebit: 50000, totalCredit: 50000, isBalanced: true,
        createdBy: 'Admin User', postedAt: '2026-09-01T10:00:00Z',
        referenceType: 'MANUAL', referenceId: null,
        entries: [
            { id: 'jve-001', jvId: 'jv-001', accountId: 'acc-017', accountCode: '5203', accountName: 'Depreciation Expense', debit: 50000, credit: 0, narration: 'Monthly depreciation' },
            { id: 'jve-002', jvId: 'jv-001', accountId: 'acc-007', accountCode: '1202', accountName: 'Accumulated Depreciation', debit: 0, credit: 50000, narration: 'Monthly depreciation' },
        ],
    },
    {
        id: 'jv-002', jvNumber: 'JV-2026-0002',
        date: '2026-09-05',
        narration: 'Salary payment for August 2026',
        status: 'POSTED',
        totalDebit: 180000, totalCredit: 180000, isBalanced: true,
        createdBy: 'Admin User', postedAt: '2026-09-05T09:00:00Z',
        referenceType: 'MANUAL', referenceId: null,
        entries: [
            { id: 'jve-003', jvId: 'jv-002', accountId: 'acc-015', accountCode: '5201', accountName: 'Salaries & Wages', debit: 180000, credit: 0, narration: 'Aug 2026 salary' },
            { id: 'jve-004', jvId: 'jv-002', accountId: 'acc-002', accountCode: '1102', accountName: 'HDFC Bank - Current A/c', debit: 0, credit: 180000, narration: 'Aug 2026 salary' },
        ],
    },
    {
        id: 'jv-003', jvNumber: 'JV-2026-0003',
        date: '2026-09-10',
        narration: 'Provision for bad debts',
        status: 'DRAFT',
        totalDebit: 25000, totalCredit: 25000, isBalanced: true,
        createdBy: 'Admin User', postedAt: null,
        referenceType: 'MANUAL', referenceId: null,
        entries: [
            { id: 'jve-005', jvId: 'jv-003', accountId: 'acc-015', accountCode: '5201', accountName: 'Salaries & Wages', debit: 25000, credit: 0, narration: 'Bad debt provision' },
            { id: 'jve-006', jvId: 'jv-003', accountId: 'acc-003', accountCode: '1103', accountName: 'Accounts Receivable (Debtors)', debit: 0, credit: 25000, narration: 'Bad debt provision' },
        ],
    },
];

export function generateNextJVNumber(): string {
    const yr = new Date().getFullYear();
    const count = mockJournalVouchers.length + 1;
    return `JV-${yr}-${String(count).padStart(4, '0')}`;
}

export function generateNextAccountCode(groupId: string): string {
    const group = mockAccountGroups.find((g) => g.id === groupId);
    if (!group) return '9999';
    const prefix = group.code.slice(0, 2);
    const existing = mockAccounts.filter((a) => a.code.startsWith(prefix)).map((a) => parseInt(a.code, 10));
    const max = existing.length > 0 ? Math.max(...existing) : parseInt(prefix + '00', 10);
    return String(max + 1);
}

// ─── Bank Reconciliation ─────────────────────────────────────────────────────

export interface MockBankAccount {
    id: string;
    accountName: string;
    bankName: string;
    accountNumber: string;
    ifscCode: string;
    branchName: string;
    currentBalance: number;
    lastReconciledDate: string | null;
    lastReconciledBalance: number;
}

export interface MockBankStatement {
    id: string;
    bankAccountId: string;
    date: string;
    description: string;
    debit: number;
    credit: number;
    closingBalance: number;
    status: 'MATCHED' | 'UNMATCHED' | 'IGNORED';
    matchedJvId: string | null;
    matchedJvNumber: string | null;
}

export interface MockOutstandingCheque {
    id: string;
    chequeNo: string;
    dateIssued: string;
    party: string;
    amount: number;
    bankAccountId: string;
    bankName: string;
    status: 'ISSUED' | 'PRESENTED' | 'CLEARED' | 'BOUNCED';
    paymentRef: string | null;
    clearedDate: string | null;
}

export interface MockReconciliationHistory {
    id: string;
    bankAccountId: string;
    reconciledDate: string;
    bankBalance: number;
    bookBalance: number;
    reconciledBalance: number;
    matchedCount: number;
    unmatchedCount: number;
    reportData: string | null;
}

export let mockBankAccounts: MockBankAccount[] = [
    {
        id: 'ba-001',
        accountName: 'HDFC Bank - Current A/c',
        bankName: 'HDFC Bank',
        accountNumber: 'XXXX1234',
        ifscCode: 'HDFC0001234',
        branchName: 'Mumbai Main',
        currentBalance: 687450,
        lastReconciledDate: '2026-08-31',
        lastReconciledBalance: 620000,
    },
    {
        id: 'ba-002',
        accountName: 'SBI Savings A/c',
        bankName: 'State Bank of India',
        accountNumber: 'XXXX5678',
        ifscCode: 'SBIN0001234',
        branchName: 'Delhi Branch',
        currentBalance: 125000,
        lastReconciledDate: null,
        lastReconciledBalance: 0,
    },
];

export let mockBankStatements: MockBankStatement[] = [
    {
        id: 'bs-001',
        bankAccountId: 'ba-001',
        date: '2026-09-01',
        description: 'NEFT CR - Metro Industries',
        debit: 0,
        credit: 100000,
        closingBalance: 720000,
        status: 'MATCHED',
        matchedJvId: 'jv-auto-001',
        matchedJvNumber: 'JV-2026-AUTO-001',
    },
    {
        id: 'bs-002',
        bankAccountId: 'ba-001',
        date: '2026-09-05',
        description: 'CHQ - Salary Aug 2026',
        debit: 180000,
        credit: 0,
        closingBalance: 540000,
        status: 'MATCHED',
        matchedJvId: 'jv-002',
        matchedJvNumber: 'JV-2026-0002',
    },
    {
        id: 'bs-003',
        bankAccountId: 'ba-001',
        date: '2026-09-07',
        description: 'NEFT DR - Steel Suppliers',
        debit: 150000,
        credit: 0,
        closingBalance: 390000,
        status: 'UNMATCHED',
        matchedJvId: null,
        matchedJvNumber: null,
    },
    {
        id: 'bs-004',
        bankAccountId: 'ba-001',
        date: '2026-09-08',
        description: 'BANK CHARGES Sep 2026',
        debit: 500,
        credit: 0,
        closingBalance: 389500,
        status: 'UNMATCHED',
        matchedJvId: null,
        matchedJvNumber: null,
    },
    {
        id: 'bs-005',
        bankAccountId: 'ba-001',
        date: '2026-09-10',
        description: 'NEFT CR - Delhi Distributors',
        debit: 0,
        credit: 75520,
        closingBalance: 465020,
        status: 'UNMATCHED',
        matchedJvId: null,
        matchedJvNumber: null,
    },
];

export let mockOutstandingCheques: MockOutstandingCheque[] = [
    {
        id: 'chq-001',
        chequeNo: 'CHQ-001',
        dateIssued: '2026-09-07',
        party: 'Steel Suppliers Pvt Ltd',
        amount: 150000,
        bankAccountId: 'ba-001',
        bankName: 'HDFC Bank',
        status: 'ISSUED',
        paymentRef: 'PYMT-2026-003',
        clearedDate: null,
    },
    {
        id: 'chq-002',
        chequeNo: 'CHQ-002',
        dateIssued: '2026-09-03',
        party: 'Plastico Industries',
        amount: 45000,
        bankAccountId: 'ba-001',
        bankName: 'HDFC Bank',
        status: 'PRESENTED',
        paymentRef: 'PYMT-2026-002',
        clearedDate: null,
    },
    {
        id: 'chq-003',
        chequeNo: 'CHQ-003',
        dateIssued: '2026-08-28',
        party: 'PowerGrid Electricity',
        amount: 22000,
        bankAccountId: 'ba-001',
        bankName: 'HDFC Bank',
        status: 'CLEARED',
        paymentRef: 'PYMT-2026-001',
        clearedDate: '2026-08-31',
    },
    {
        id: 'chq-004',
        chequeNo: 'CHQ-004',
        dateIssued: '2026-09-01',
        party: 'Rent Payment — Mumbai',
        amount: 48000,
        bankAccountId: 'ba-001',
        bankName: 'HDFC Bank',
        status: 'ISSUED',
        paymentRef: 'PYMT-2026-004',
        clearedDate: null,
    },
];

export let mockReconciliationHistory: MockReconciliationHistory[] = [
    {
        id: 'rec-001',
        bankAccountId: 'ba-001',
        reconciledDate: '2026-08-31',
        bankBalance: 620000,
        bookBalance: 615000,
        reconciledBalance: 620000,
        matchedCount: 12,
        unmatchedCount: 2,
        reportData: null,
    },
    {
        id: 'rec-002',
        bankAccountId: 'ba-001',
        reconciledDate: '2026-07-31',
        bankBalance: 580000,
        bookBalance: 582500,
        reconciledBalance: 580000,
        matchedCount: 10,
        unmatchedCount: 3,
        reportData: null,
    },
    {
        id: 'rec-003',
        bankAccountId: 'ba-001',
        reconciledDate: '2026-06-30',
        bankBalance: 540000,
        bookBalance: 538000,
        reconciledBalance: 540000,
        matchedCount: 9,
        unmatchedCount: 1,
        reportData: null,
    },
];

// ─── Fixed Assets ─────────────────────────────────────────────────────────────

export interface MockFixedAsset {
    id: string;
    assetCode: string;
    assetName: string;
    category: 'PLANT_MACHINERY' | 'VEHICLE' | 'COMPUTER' | 'FURNITURE' | 'BUILDING' | 'OTHER';
    description: string;
    purchaseDate: string;
    purchaseAmount: number;
    supplier: string | null;
    salvageValue: number;
    usefulLifeYears: number;
    depreciationMethod: 'SLM' | 'WDV';
    depreciationRate: number;
    location: string;
    assignedTo: string | null;
    bookValue: number;
    accumulatedDepreciation: number;
    status: 'ACTIVE' | 'DISPOSED' | 'SCRAPPED';
    disposalDate: string | null;
    disposalValue: number | null;
    gainLoss: number | null;
    amcContractId: string | null;
    insuranceExpiry: string | null;
    glAccountId: string;
    notes: string | null;
}

export interface MockDepreciationEntry {
    id: string;
    assetId: string;
    assetName: string;
    period: string;
    depreciationAmount: number;
    openingBookValue: number;
    closingBookValue: number;
    method: 'SLM' | 'WDV';
    jvId: string | null;
    jvNumber: string | null;
    status: 'POSTED' | 'PENDING';
}

export let mockFixedAssets: MockFixedAsset[] = [
    {
        id: 'fa-001', assetCode: 'FA-2026-001',
        assetName: 'Press Machine PM-01',
        category: 'PLANT_MACHINERY',
        description: '100-ton hydraulic press machine',
        purchaseDate: '2024-01-15', purchaseAmount: 2500000,
        supplier: 'PressServ Solutions', salvageValue: 250000,
        usefulLifeYears: 10, depreciationMethod: 'WDV',
        depreciationRate: 15,
        location: 'Press Shop Floor A',
        assignedTo: 'Production Department',
        bookValue: 2006250, accumulatedDepreciation: 493750,
        status: 'ACTIVE',
        disposalDate: null, disposalValue: null, gainLoss: null,
        amcContractId: 'amc-001', insuranceExpiry: '2027-01-14',
        glAccountId: 'acc-006',
        notes: 'Covered under 3-year AMC',
    },
    {
        id: 'fa-002', assetCode: 'FA-2026-002',
        assetName: 'Injection Moulding Machine IM-01',
        category: 'PLANT_MACHINERY',
        description: '200-ton injection moulding machine',
        purchaseDate: '2023-06-20', purchaseAmount: 1800000,
        supplier: 'Mould Tech International', salvageValue: 180000,
        usefulLifeYears: 10, depreciationMethod: 'WDV',
        depreciationRate: 15,
        location: 'Moulding Section B',
        assignedTo: 'Production Department',
        bookValue: 1246050, accumulatedDepreciation: 553950,
        status: 'ACTIVE',
        disposalDate: null, disposalValue: null, gainLoss: null,
        amcContractId: 'amc-002', insuranceExpiry: '2026-06-19',
        glAccountId: 'acc-006',
        notes: 'Insurance expired — needs renewal',
    },
    {
        id: 'fa-003', assetCode: 'FA-2026-003',
        assetName: 'Company Vehicle - Delivery Van',
        category: 'VEHICLE',
        description: 'Tata Ace delivery van MH-04-AB-1234',
        purchaseDate: '2024-08-01', purchaseAmount: 650000,
        supplier: 'Tata Motors Showroom', salvageValue: 50000,
        usefulLifeYears: 8, depreciationMethod: 'SLM',
        depreciationRate: 12.5,
        location: 'Company Premises',
        assignedTo: 'Dispatch Department',
        bookValue: 512500, accumulatedDepreciation: 137500,
        status: 'ACTIVE',
        disposalDate: null, disposalValue: null, gainLoss: null,
        amcContractId: null, insuranceExpiry: '2027-07-31',
        glAccountId: 'acc-006', notes: null,
    },
    {
        id: 'fa-004', assetCode: 'FA-2026-004',
        assetName: 'Office Computers (5 units)',
        category: 'COMPUTER',
        description: 'Dell OptiPlex 5 units with monitors',
        purchaseDate: '2024-04-01', purchaseAmount: 250000,
        supplier: 'Dell India', salvageValue: 25000,
        usefulLifeYears: 5, depreciationMethod: 'SLM',
        depreciationRate: 20,
        location: 'Admin Office',
        assignedTo: 'Admin Department',
        bookValue: 160000, accumulatedDepreciation: 90000,
        status: 'ACTIVE',
        disposalDate: null, disposalValue: null, gainLoss: null,
        amcContractId: null, insuranceExpiry: null,
        glAccountId: 'acc-006', notes: null,
    },
    {
        id: 'fa-005', assetCode: 'FA-2026-005',
        assetName: 'Old Lathe Machine LT-001',
        category: 'PLANT_MACHINERY',
        description: 'Manual lathe machine (replaced by CNC)',
        purchaseDate: '2018-01-01', purchaseAmount: 350000,
        supplier: 'HMT Machine Tools', salvageValue: 0,
        usefulLifeYears: 10, depreciationMethod: 'SLM',
        depreciationRate: 10,
        location: 'Tool Room (decommissioned)',
        assignedTo: null,
        bookValue: 0, accumulatedDepreciation: 350000,
        status: 'SCRAPPED',
        disposalDate: '2026-08-01', disposalValue: 15000,
        gainLoss: 15000,
        amcContractId: null, insuranceExpiry: null,
        glAccountId: 'acc-006',
        notes: 'Scrapped. Scrap value Rs.15,000 received.',
    },
];

export let mockDepreciationEntries: MockDepreciationEntry[] = [
    {
        id: 'de-001', assetId: 'fa-001',
        assetName: 'Press Machine PM-01',
        period: '2026-09',
        depreciationAmount: 25078,
        openingBookValue: 2006250, closingBookValue: 1981172,
        method: 'WDV', jvId: null, jvNumber: null,
        status: 'PENDING',
    },
    {
        id: 'de-002', assetId: 'fa-001',
        assetName: 'Press Machine PM-01',
        period: '2026-08',
        depreciationAmount: 25391,
        openingBookValue: 2031641, closingBookValue: 2006250,
        method: 'WDV', jvId: 'jv-001', jvNumber: 'JV-2026-0001',
        status: 'POSTED',
    },
    {
        id: 'de-003', assetId: 'fa-002',
        assetName: 'Injection Moulding Machine IM-01',
        period: '2026-09',
        depreciationAmount: 15576,
        openingBookValue: 1246050, closingBookValue: 1230474,
        method: 'WDV', jvId: null, jvNumber: null,
        status: 'PENDING',
    },
    {
        id: 'de-004', assetId: 'fa-003',
        assetName: 'Company Vehicle - Delivery Van',
        period: '2026-09',
        depreciationAmount: 6250,
        openingBookValue: 512500, closingBookValue: 506250,
        method: 'SLM', jvId: null, jvNumber: null,
        status: 'PENDING',
    },
    {
        id: 'de-005', assetId: 'fa-004',
        assetName: 'Office Computers (5 units)',
        period: '2026-09',
        depreciationAmount: 3750,
        openingBookValue: 160000, closingBookValue: 156250,
        method: 'SLM', jvId: null, jvNumber: null,
        status: 'PENDING',
    },
];

export function generateNextAssetCode(): string {
    const yr = new Date().getFullYear();
    const count = mockFixedAssets.length + 1;
    return `FA-${yr}-${String(count).padStart(3, '0')}`;
}

export function calculateAssetDepreciation(asset: MockFixedAsset, months: number = 1): { monthlyDep: number; closingBV: number } {
    if (asset.status !== 'ACTIVE') return { monthlyDep: 0, closingBV: asset.bookValue };
    if (asset.depreciationMethod === 'SLM') {
        const annualDep = (asset.purchaseAmount - asset.salvageValue) / asset.usefulLifeYears;
        const monthlyDep = Math.round(annualDep / 12);
        const newBV = Math.max(asset.salvageValue, asset.bookValue - monthlyDep * months);
        return { monthlyDep, closingBV: newBV };
    }
    // WDV
    const monthlyRate = asset.depreciationRate / 100 / 12;
    let bv = asset.bookValue;
    let totalDep = 0;
    for (let i = 0; i < months; i++) {
        const dep = Math.round(bv * monthlyRate);
        totalDep += dep;
        bv -= dep;
    }
    return { monthlyDep: totalDep, closingBV: bv };
}

// ─── Multi-Currency ───────────────────────────────────────────────────────────

export interface MockCurrency {
    id: string;
    code: string;
    name: string;
    symbol: string;
    exchangeRate: number;
    lastUpdated: string;
    isActive: boolean;
}

export interface MockForexEntry {
    id: string;
    invoiceId: string;
    invoiceNumber: string;
    customerId: string;
    customerName: string;
    currencyCode: string;
    currencySymbol: string;
    foreignAmount: number;
    exchangeRateUsed: number;
    inrAmount: number;
    settlementRate: number | null;
    settlementInrAmount: number | null;
    forexGainLoss: number | null;
    type: 'RECEIVABLE' | 'PAYABLE';
}

export let mockCurrencies: MockCurrency[] = [
    { id: 'cur-001', code: 'USD', name: 'US Dollar', symbol: '$', exchangeRate: 84.25, lastUpdated: '2026-09-07', isActive: true },
    { id: 'cur-002', code: 'EUR', name: 'Euro', symbol: '€', exchangeRate: 93.10, lastUpdated: '2026-09-07', isActive: true },
    { id: 'cur-003', code: 'GBP', name: 'British Pound', symbol: '£', exchangeRate: 108.50, lastUpdated: '2026-09-07', isActive: true },
    { id: 'cur-004', code: 'AED', name: 'UAE Dirham', symbol: 'AED', exchangeRate: 22.95, lastUpdated: '2026-09-07', isActive: true },
    { id: 'cur-005', code: 'SGD', name: 'Singapore Dollar', symbol: 'SGD', exchangeRate: 63.20, lastUpdated: '2026-09-07', isActive: false },
];

export let mockForexEntries: MockForexEntry[] = [
    {
        id: 'fx-001', invoiceId: 'inv-fx-001', invoiceNumber: 'INV-EXP-2026-0001',
        customerId: 'pty-cust-001', customerName: 'Metro International LLC',
        currencyCode: 'USD', currencySymbol: '$',
        foreignAmount: 5000, exchangeRateUsed: 84.00, inrAmount: 420000,
        settlementRate: 84.50, settlementInrAmount: 422500, forexGainLoss: 2500,
        type: 'RECEIVABLE',
    },
    {
        id: 'fx-002', invoiceId: 'inv-fx-002', invoiceNumber: 'INV-EXP-2026-0002',
        customerId: 'pty-cust-002', customerName: 'Euro Parts GmbH',
        currencyCode: 'EUR', currencySymbol: '€',
        foreignAmount: 3000, exchangeRateUsed: 93.00, inrAmount: 279000,
        settlementRate: null, settlementInrAmount: null, forexGainLoss: null,
        type: 'RECEIVABLE',
    },
];